import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { GlowButton } from "@/components/ui/GlowButton";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface NavLink {
  label: string;
  href: string;
}

const primaryItems: NavLink[] = [
  { label: "Platform", href: "/platform" },
  { label: "Founders & CEOs", href: "/for-founders" },
];

const investorItems: NavLink[] = [
  { label: "Investors", href: "/investors" },
  { label: "Advisers", href: "/advisors" },
];

const plainItems: NavLink[] = [
  { label: "Security", href: "/security" },
  { label: "Pricing", href: "/pricing" },
  { label: "Contact", href: "/contact" },
];

const APP_URL = import.meta.env.VITE_APP_URL || "https://app.provenance365.com";
const SIGN_IN_URL = `${APP_URL.replace(/\/$/, "")}/sign-in`;

/** Desktop disclosure navigation: hover-friendly, keyboard operable, and state is announced. */
const NavDropdown = ({ label, items }: { label: string; items: NavLink[] }) => {
  const [open, setOpen] = useState(false);
  const menuId = `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
          (event.currentTarget.querySelector("button") as HTMLButtonElement | null)?.focus();
        }
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      <div
        id={menuId}
        className={cn(
          "absolute left-1/2 top-full -translate-x-1/2 pt-3 transition-opacity",
          open ? "visible opacity-100" : "invisible opacity-0",
        )}
      >
        <div className="min-w-[240px] rounded-2xl border border-border bg-background p-2 shadow-xl">
          {items.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href.split("#")[0] && (!href.includes("#") || location.hash === `#${href.split("#")[1]}`);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl"
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo-provenance-icon.svg" alt="PROVENANCE 365" className="h-9 w-9 shrink-0" />
          <span className="font-display text-lg font-bold tracking-tight text-black">PROVENANCE 365</span>
        </Link>

        <div className="hidden items-center gap-5 xl:flex">
          {primaryItems.map((item) => (
            <Link key={item.href} to={item.href} className={cn("text-sm font-medium transition-colors hover:text-foreground", isActive(item.href) ? "text-foreground" : "text-muted-foreground")}>
              {item.label}
            </Link>
          ))}
          <NavDropdown label="Investors & advisers" items={investorItems} />
          {plainItems.filter((item) => item.href !== "/contact").map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                "text-sm font-medium transition-colors hover:text-foreground",
                isActive(item.href) ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 xl:flex">
          {user ? (
            <>
              <GlowButton variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                Dashboard
              </GlowButton>
              <GlowButton variant="secondary" size="sm" onClick={handleSignOut}>
                Sign out
              </GlowButton>
            </>
          ) : (
            <>
              <GlowButton variant="ghost" size="sm" onClick={() => { window.location.href = SIGN_IN_URL; }}>
                Sign in
              </GlowButton>
              <GlowButton variant="primary" size="sm" onClick={() => navigate("/contact")}>
                Book a walkthrough
              </GlowButton>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-foreground"
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-border bg-background px-4 py-6 xl:hidden"
        >
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {[...primaryItems, ...investorItems].map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-3 text-sm font-medium text-muted-foreground"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-3 border-t border-border pt-4">
              {plainItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="text-sm font-medium text-muted-foreground"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-3 border-t border-border pt-4">
              {user ? (
                <>
                  <GlowButton variant="ghost" size="sm" onClick={() => { setIsOpen(false); navigate("/dashboard"); }}>
                    Dashboard
                  </GlowButton>
                  <GlowButton variant="secondary" size="sm" onClick={() => { setIsOpen(false); handleSignOut(); }}>
                    Sign out
                  </GlowButton>
                </>
              ) : (
                <>
                  <GlowButton variant="ghost" size="sm" onClick={() => { window.location.href = SIGN_IN_URL; }}>
                    Sign in
                  </GlowButton>
                  <GlowButton variant="primary" size="sm" onClick={() => { setIsOpen(false); navigate("/contact"); }}>
                    Book a walkthrough
                  </GlowButton>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};
